import { Link } from 'react-router';
import { SITE_URL } from '../content/types';
import { PageMeta } from '../components/PageMeta';
import { FAQSection } from '../components/FAQSection';
import type { FAQItem } from '../components/FAQSection';
import { MarketingHeader } from '../components/marketing/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';

/** Terminal-style code block with the same chrome used on the landing page. */
function CodeWindow({ filename, children }: { filename: string; children: React.ReactNode }) {
  return (
    <div className="relative bg-slate-900 rounded-2xl p-4 sm:p-6 shadow-2xl ring-1 ring-white/10 overflow-hidden">
      <div className="flex items-center gap-2 mb-5">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500/80" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <div className="w-3 h-3 rounded-full bg-green-500/80" />
        </div>
        <span className="ml-3 text-slate-500 text-xs font-mono">{filename}</span>
      </div>
      <pre className="text-xs sm:text-sm overflow-x-auto leading-relaxed">
        <code className="text-slate-300">{children}</code>
      </pre>
    </div>
  );
}

const capabilities = [
  {
    icon: '🔑',
    title: 'Scoped API keys',
    description:
      'Mint org-wide or brand-scoped keys with granular scopes — collect, read, write, forms — plus optional expiration dates. Revoke instantly.',
  },
  {
    icon: '📄',
    title: 'REST + OpenAPI 3.1',
    description:
      'A clean JSON API described by a published OpenAPI 3.1 spec. Generate typed clients in any language, or explore it with your favorite tooling.',
  },
  {
    icon: '🪝',
    title: 'Webhooks & automations',
    description:
      'Fire webhooks on feedback.created and page_response.created with custom headers, body templates, and encrypted secrets interpolation.',
  },
  {
    icon: '🧩',
    title: 'MCP server',
    description:
      'Connect Claude, Cursor, or any MCP client to the /mcp endpoint — submit feedback, query stats, and list inboxes without writing HTTP calls.',
  },
  {
    icon: '🔍',
    title: 'Semantic search API',
    description:
      'Search feedback by meaning, not just keywords. Vector embeddings let you find "checkout is confusing" when the user wrote "paying is hard".',
  },
  {
    icon: '⚡',
    title: 'Zapier integration',
    description:
      'Triggers for new feedback and page responses, plus actions to submit and update feedback — connect 6,000+ apps without code.',
  },
  {
    icon: '🔗',
    title: 'Issue tracker linking',
    description:
      'Link feedback to Jira, GitHub, Linear, Asana, or Redmine issues via the API and keep your triage workflow in the tools you already use.',
  },
  {
    icon: '🖥️',
    title: 'Drop-in widget & React',
    description:
      'A one-line script tag or a typed React package for in-app collection — both feed the same API and inboxes as your server-side events.',
  },
];

const endpoints = [
  { method: 'POST', path: '/collect', desc: 'Submit feedback with an API key (widget, server, or agent)' },
  { method: 'GET', path: '/organizations/:orgId/feedback', desc: 'List + filter feedback by type, score, date, inbox, source' },
  { method: 'POST', path: '/organizations/:orgId/search', desc: 'Semantic search across all collected feedback' },
  { method: 'PATCH', path: '/organizations/:orgId/feedback/:id', desc: 'Update status or link an external issue tracker' },
  { method: 'GET', path: '/v1/organizations/:orgId/feedback-pages/:id', desc: 'Read form structure for programmatic submission' },
  { method: 'POST', path: '/mcp', desc: 'Model Context Protocol endpoint for AI agents' },
];

const developerFAQs: FAQItem[] = [
  {
    question: 'Is there a free tier for the feedback API?',
    answer:
      'Yes. The Starter plan is free forever and includes full API access, 1 inbox, and 5,000 events per month. No credit card required — create an API key and start posting feedback in minutes.',
  },
  {
    question: 'How does authentication work?',
    answer:
      'Requests authenticate with an API key sent in the X-Pollenate-Key header. Keys can be scoped to an entire organization or a single brand, restricted by scope (collect, read, write, forms), and given expiration dates.',
  },
  {
    question: 'Is there an OpenAPI specification?',
    answer:
      'Yes. A complete OpenAPI 3.1 spec is published at pollenate.dev/openapi.yaml covering every public endpoint, auth scheme, and schema — ready for codegen tools and API clients.',
  },
  {
    question: 'Do you support webhooks?',
    answer:
      'Yes. Automations can deliver webhooks on feedback.created and page_response.created events with custom headers, templated JSON bodies, and encrypted secret interpolation for signing or authorization headers.',
  },
  {
    question: 'Can AI agents submit and query feedback?',
    answer:
      'Yes. Pollenate ships a Model Context Protocol (MCP) server at /mcp with tools to submit feedback, list inboxes and brands, fetch feedback, and query stats. Claude Desktop, Cursor, and any MCP client connect directly.',
  },
  {
    question: 'What are the rate limits?',
    answer:
      'Limits are plan-based daily allowances on feedback events and search queries rather than per-second throttles. The API returns clear 429 responses with your limit, plan, and reset time when a cap is reached.',
  },
];

/**
 * Developer-focused landing page for the "Developer Feedback API" niche.
 * Serves as the message-matched destination for technical ad campaigns.
 */
export function DevelopersPage() {
  const url = `${SITE_URL}/developers`;

  const webApiLd = {
    '@context': 'https://schema.org',
    '@type': 'WebAPI',
    name: 'Pollenate Feedback API',
    description:
      'REST API for collecting, querying, and acting on user feedback — scoped API keys, webhooks, semantic search, and an MCP server for AI agents.',
    documentation: `${SITE_URL}/docs`,
    termsOfService: `${SITE_URL}/terms`,
    provider: { '@type': 'Organization', name: 'Pollenate', url: SITE_URL },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Developers', item: url },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: developerFAQs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-neutral-950">
      <PageMeta
        title="Developer Feedback API — Pollenate"
        description="Collect and query user feedback with a REST API, scoped keys, webhooks, semantic search, and an MCP server for AI agents. OpenAPI 3.1 spec. Free tier."
        url={url}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webApiLd).replace(/</g, '\\u003c') }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd).replace(/</g, '\\u003c') }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd).replace(/</g, '\\u003c') }} />

      <MarketingHeader />

      <main id="main-content">
        {/* ---- Hero ---- */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                Developer Feedback API
              </p>
              <h1 className="mt-2 text-3xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
                The feedback API built for developers
              </h1>
              <p className="mt-4 text-lg text-slate-600 dark:text-neutral-400">
                One POST request to collect feedback from your app, server, or AI agent. Query it, search it by
                meaning, and pipe it anywhere with webhooks. No SDK lock-in, no per-seat pricing.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/login?mode=signup"
                  className="inline-flex items-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:from-amber-600 hover:to-orange-600 transition-colors"
                >
                  Get your API key — free
                </Link>
                <Link
                  to="/docs#api-overview"
                  className="inline-flex items-center rounded-lg border border-slate-300 dark:border-neutral-700 px-5 py-3 text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-neutral-800 transition-colors"
                >
                  Read the API docs
                </Link>
              </div>
              <p className="mt-4 text-sm text-slate-500 dark:text-neutral-400">
                Free tier: 5,000 events/month · Full API access · No credit card
              </p>
            </div>

            <div className="relative min-w-0">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 rounded-3xl blur-2xl pointer-events-none" />
              <CodeWindow filename="collect-feedback.sh">
                <span className="text-slate-500"># Submit feedback from anywhere</span>
                {'\n'}
                <span className="text-amber-300">curl</span>
                <span className="text-slate-300">{' -X POST https://api.pollenate.dev/collect \\'}</span>
                {'\n'}
                <span className="text-slate-300">{'  -H '}</span>
                <span className="text-green-400">"X-Pollenate-Key: pk_live_..."</span>
                <span className="text-slate-300">{' \\'}</span>
                {'\n'}
                <span className="text-slate-300">{'  -d '}</span>
                <span className="text-green-400">{"'"}</span>
                <span className="text-pink-400">{'{'}</span>
                {'\n'}
                <span className="text-slate-300">{'    '}</span>
                <span className="text-amber-300">"inboxKey"</span>
                <span className="text-slate-300">{': '}</span>
                <span className="text-green-400">"my-app"</span>
                <span className="text-slate-300">,</span>
                {'\n'}
                <span className="text-slate-300">{'    '}</span>
                <span className="text-amber-300">"type"</span>
                <span className="text-slate-300">{': '}</span>
                <span className="text-green-400">"nps"</span>
                <span className="text-slate-300">,</span>
                {'\n'}
                <span className="text-slate-300">{'    '}</span>
                <span className="text-amber-300">"score"</span>
                <span className="text-slate-300">{': '}</span>
                <span className="text-purple-400">9</span>
                <span className="text-slate-300">,</span>
                {'\n'}
                <span className="text-slate-300">{'    '}</span>
                <span className="text-amber-300">"comment"</span>
                <span className="text-slate-300">{': '}</span>
                <span className="text-green-400">"Love the new dashboard"</span>
                {'\n'}
                <span className="text-pink-400">{'  }'}</span>
                <span className="text-green-400">{"'"}</span>
                {'\n\n'}
                <span className="text-slate-500"># 201 Created</span>
                {'\n'}
                <span className="text-pink-400">{'{'}</span>
                <span className="text-slate-300">{' '}</span>
                <span className="text-amber-300">"id"</span>
                <span className="text-slate-300">{': '}</span>
                <span className="text-green-400">"f3a91c..."</span>
                <span className="text-slate-300">{', '}</span>
                <span className="text-amber-300">"success"</span>
                <span className="text-slate-300">{': '}</span>
                <span className="text-purple-400">true</span>
                <span className="text-slate-300">{' '}</span>
                <span className="text-pink-400">{'}'}</span>
              </CodeWindow>
            </div>
          </div>
        </section>

        {/* ---- Capabilities ---- */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16 bg-white dark:bg-neutral-900/40 border-y border-slate-200 dark:border-neutral-800">
          <div className="max-w-6xl mx-auto">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                Everything is an API
              </h2>
              <p className="mt-3 text-lg text-slate-600 dark:text-neutral-400">
                The dashboard is optional. Every capability — collection, querying, search, automations — is exposed
                through the same API your widgets use.
              </p>
            </div>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {capabilities.map((cap) => (
                <div
                  key={cap.title}
                  className="rounded-2xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40 flex items-center justify-center text-xl">
                    {cap.icon}
                  </div>
                  <h3 className="mt-4 font-semibold text-slate-900 dark:text-white">{cap.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-neutral-400">{cap.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- Endpoint overview ---- */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                A surface you can learn in an afternoon
              </h2>
              <p className="mt-3 text-lg text-slate-600 dark:text-neutral-400">
                Predictable REST endpoints, JSON in and out, documented in the{' '}
                <a
                  href="https://pollenate.dev/openapi.yaml"
                  className="text-amber-600 dark:text-amber-400 hover:underline"
                >
                  OpenAPI 3.1 spec
                </a>
                .
              </p>
            </div>
            <div className="mt-10 rounded-2xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 divide-y divide-slate-100 dark:divide-neutral-800 overflow-hidden">
              {endpoints.map((ep) => (
                <div key={`${ep.method} ${ep.path}`} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-5 py-4">
                  <span
                    className={`inline-flex w-16 justify-center rounded-md px-2 py-1 text-xs font-bold font-mono ${
                      ep.method === 'GET'
                        ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400'
                        : ep.method === 'PATCH'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                    }`}
                  >
                    {ep.method}
                  </span>
                  <code className="text-sm font-mono text-slate-900 dark:text-white">{ep.path}</code>
                  <span className="text-sm text-slate-500 dark:text-neutral-400 sm:ml-auto sm:text-right">{ep.desc}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-center text-sm text-slate-500 dark:text-neutral-400">
              Plus inboxes, brands, feedback pages, notes, automations, and API key management —{' '}
              <Link to="/docs#api-overview" className="text-amber-600 dark:text-amber-400 hover:underline">
                see the full reference &rarr;
              </Link>
            </p>
          </div>
        </section>

        {/* ---- MCP callout ---- */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16 bg-white dark:bg-neutral-900/40 border-y border-slate-200 dark:border-neutral-800">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                AI-native by design
              </p>
              <h2 className="mt-2 text-2xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                Your agents get their own endpoint
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-neutral-400">
                The built-in MCP server lets Claude Desktop, Cursor, and any Model Context Protocol client submit
                feedback, query stats, and browse inboxes — authenticated with the same scoped API keys.
              </p>
              <div className="mt-6">
                <Link
                  to="/ai"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400 hover:underline"
                >
                  Explore the AI-native platform &rarr;
                </Link>
              </div>
            </div>
            <div className="relative min-w-0">
              <CodeWindow filename="mcp-config.json">
                <span className="text-pink-400">{'{'}</span>
                {'\n'}
                <span className="text-slate-300">{'  '}</span>
                <span className="text-amber-300">"mcpServers"</span>
                <span className="text-slate-300">{': {'}</span>
                {'\n'}
                <span className="text-slate-300">{'    '}</span>
                <span className="text-amber-300">"pollenate"</span>
                <span className="text-slate-300">{': {'}</span>
                {'\n'}
                <span className="text-slate-300">{'      '}</span>
                <span className="text-amber-300">"url"</span>
                <span className="text-slate-300">{': '}</span>
                <span className="text-green-400">"https://api.pollenate.dev/mcp"</span>
                <span className="text-slate-300">,</span>
                {'\n'}
                <span className="text-slate-300">{'      '}</span>
                <span className="text-amber-300">"headers"</span>
                <span className="text-slate-300">{': { '}</span>
                <span className="text-amber-300">"X-Pollenate-Key"</span>
                <span className="text-slate-300">{': '}</span>
                <span className="text-green-400">"pk_live_..."</span>
                <span className="text-slate-300">{' }'}</span>
                {'\n'}
                <span className="text-slate-300">{'    }'}</span>
                {'\n'}
                <span className="text-slate-300">{'  }'}</span>
                {'\n'}
                <span className="text-pink-400">{'}'}</span>
              </CodeWindow>
            </div>
          </div>
        </section>

        {/* ---- FAQ ---- */}
        <FAQSection faqs={developerFAQs} id="developers" injectSchema={false} />

        {/* ---- Final CTA ---- */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20">
          <div className="max-w-3xl mx-auto text-center rounded-3xl border border-amber-200/60 dark:border-amber-700/40 bg-gradient-to-b from-amber-50 to-orange-50/50 dark:from-amber-950/30 dark:to-orange-950/20 px-6 py-12">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              Ship feedback collection today
            </h2>
            <p className="mt-3 text-lg text-slate-600 dark:text-neutral-400">
              Sign up, create an API key, and make your first request in under five minutes.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/login?mode=signup"
                className="inline-flex items-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:from-amber-600 hover:to-orange-600 transition-colors"
              >
                Start free
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center rounded-lg border border-slate-300 dark:border-neutral-700 px-5 py-3 text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-neutral-800 transition-colors"
              >
                View pricing
              </Link>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
